import React, { useState, useImperativeHandle, useRef, useEffect, InputHTMLAttributes } from 'react';
import { cx } from '@emotion/css';
import { useMemoizedId } from '../utils';

import cvar from '../theme/cvar';
import CloseSvg from '../icons/CloseSvg';
import SuccessSvg from '../icons/SuccessSvg';
import PencilSvg from '../icons/PencilSvg';
import {
  errorMessage,
  formControl,
  inlineEdit,
  inputBaseStyle,
  labelStyle,
  rightAddOn,
  sizeStyle,
  successIconWrapper,
} from './styles';
import Check from '../icons/Check';
import { InlineButton } from '../internal';
import { PublicComponentProps } from '../types';

type InputBaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

export type TextSize = 'default' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'small';

export interface InlineEditProps extends InputBaseProps, PublicComponentProps {
  /**
   * ID prop for the input field.
   */
  id?: string;

  /**
   * Floating label for an input field. Use either this or placeholder but not both.
   */
  label?: string;

  /**
   * Determines the size of both the display text and the edit text.
   */
  size?: TextSize;

  /**
   * Determines the HTML5 input type of the input. One of text, number, password, url, search, email, or tel.
   * You can also provide textarea to get a textarea instead of an input element.
   */
  type?: 'text' | 'number' | 'password' | 'url' | 'search' | 'email' | 'tel' | 'textarea';

  disabled?: boolean;

  /**
   * Provides an optional override for localized warning messages for required fields
   */
  requiredWarningMessage?: string;

  /**
   * Callback that fires on a successful save of the value in the input. Provides the input's value and name.
   */
  onSave?: (
    inputTarget: {
      value?: string;
      name?: string;
    },
    event: MouseEvent | KeyboardEvent,
  ) => void;

  /**
   * If provided this callback fires when the user cancels the changes. Will provide the most recent changes, when the cancel happened.
   */
  onCancel?: (
    inputTarget: {
      value?: string;
      name?: string;
    },
    event: MouseEvent | KeyboardEvent,
  ) => void;

  /**
   * If provided and onChange is not provided, will be called on each text change to validate the input.
   */
  validateInput?: (text: string) => string | null;

  /**
   * Specifies the minimum width of the control.  Defaults to 130px.
   */
  minWidth?: number | null;

  /**
   * Optional callback to be notified when the editing state changes
   */
  onEditStateChange?: (isEditing: boolean) => void;
}

const noop = () => {};

const enterKeyCode = 13;

const escKeyCode = 27;

const isTextTruthy = (val: any) => val !== undefined && val !== null && val.toString() !== '';

export const InlineEdit = React.forwardRef<HTMLInputElement, any>(
  (
    {
      disabled,
      forwardedRef,
      id,
      label = '',
      minWidth = 130,
      name = '',
      placeholder,
      required = false,
      requiredWarningMessage = 'This Field is Required',
      size = 'default',
      type = 'text',
      validateInput = noop,
      value = '',
      onBlur = noop,
      onCancel = noop,
      onChange,
      onEditStateChange = noop,
      onFocus = noop,
      onSave,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    // States for Inline Edit
    const [localValue, setLocalValue] = useState(value);
    const [showInput, setShowInput] = useState(false);
    const [inputWidth, setInputWidth] = useState();
    const [savedTimer, setSavedTimer] = useState<any>();
    const [contentJustSaved, setContentJustSaved] = useState(false);
    const [validationError, setValidationError] = useState();

    const inputId = useMemoizedId({ id, label });

    // ensure we clear any timers in case component unmounts.
    useEffect(() => () => clearTimeout(savedTimer), [savedTimer]);

    const previousShowInput = useRef(showInput);
    useEffect(() => {
      if (previousShowInput.current !== showInput) {
        onEditStateChange(showInput);
      }
      previousShowInput.current = showInput;
    }, [showInput, onEditStateChange]);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      setValidationError(validateInput(localValue));
    }, [localValue, validateInput]);

    const hasOnChange = Boolean(onChange);
    const displayValue = hasOnChange ? value : localValue;

    useEffect(() => {
      if (!isTextTruthy(displayValue) && required) {
        setValidationError(requiredWarningMessage);
      }
    }, [displayValue, required, requiredWarningMessage]);

    const inputRef = useRef<any>();

    /**
     * This hook is used to customize the instance value exposed to the parent for the ref.
     * It must always be used with React.forwardRef. This gives us control of where to pass the ref prop.
     * Whatever ref is passed to Inline Edit will receive the inputRef instance.
     * This pattern supports all methods of creating refs.
     */
    useImperativeHandle(forwardedRef, () => inputRef.current);

    // used to calculate sizing for input to grow with text
    const valueSizeRef = useRef<any>() || ref;
    const placeholderSizeRef = useRef<any>();
    const errorMessageRef = useRef<any>();

    const isTextArea = type === 'textarea';

    useEffect(() => {
      // If there's an onChange handler, the save and cancel buttons are not shown, so the additional
      //   padding is not needed. However, there's still an additional 5px that need to be added.
      const extraPadding = hasOnChange ? 5 : 70;
      const pencilPadding = 25;

      let newWidth = isTextTruthy(displayValue)
        ? valueSizeRef.current.scrollWidth
        : placeholderSizeRef.current
        ? placeholderSizeRef.current.scrollWidth
        : 0;

      const errorMessageWidth = errorMessageRef.current ? errorMessageRef.current.scrollWidth : 0;

      newWidth = Math.max(newWidth, errorMessageWidth);

      newWidth += showInput ? extraPadding : pencilPadding;

      if (newWidth < minWidth) {
        newWidth = minWidth;
      }

      setInputWidth(newWidth);
    }, [displayValue, hasOnChange, valueSizeRef, placeholderSizeRef, minWidth, showInput, validationError]);

    useEffect(() => {
      if (showInput && isTextArea) {
        // Set the cursor to the end of the text and scroll to the end
        inputRef.current.setSelectionRange(inputRef.current.textContent.length, inputRef.current.textContent.length);
        inputRef.current.scrollTop = inputRef.current.scrollHeight;
      }
    }, [isTextArea, showInput]);

    const showSuccess = () => {
      setContentJustSaved(true);
      setShowInput(false);
      setSavedTimer(
        setTimeout(() => {
          setContentJustSaved(false);
        }, 1000),
      );
    };

    const handleBlur = (e: any) => {
      if (hasOnChange) {
        setShowInput(false);
        showSuccess();
      }
      onBlur(e);
    };

    const handleFocus = (e: any) => {
      onFocus(e);
    };

    const handleChange = (e: any) => {
      const inputValue = e.target.value;
      if (hasOnChange) {
        onChange(e);
      } else {
        setLocalValue(inputValue);
      }
    };

    const handleSaveClick = (e: any) => {
      if (!hasOnChange) {
        if (validationError) {
          return;
        }
        onSave({ value: localValue, name }, e);
      }
      showSuccess();
    };

    // Optional to handle how we cancel our input
    const handleCancelClick = (e: any) => {
      onCancel({ value: localValue, name }, e);
      setLocalValue(value);
      setShowInput(false);
    };

    const handleKeyDown = (e: any) => {
      e.stopPropagation();
      if (e.keyCode === enterKeyCode) {
        handleSaveClick(e);
      } else if (e.keyCode === escKeyCode) {
        handleCancelClick(e);
      }
    };

    const handleActiveClick = () => {
      setShowInput(true);
    };

    const inputProps = {
      ...rest,
      name,
      type,
      value: displayValue,
      label,
      placeholder,
      id: inputId,
      onKeyDown: handleKeyDown,
      ref: inputRef,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onChange: handleChange,
      autoFocus: true,
    };

    const inputStyle = {
      marginBottom: '20px',
      width: showInput ? `${inputWidth}px` : null,
      ...style,
    };

    const parentClassName = cx(inlineEdit, { required }, className);

    const inlineInput = (
      <>
        {isTextArea ? (
          <textarea
            className={cx(size, formControl)}
            {...inputProps}
            style={{ marginTop: '17px', padding: '16px', minWidth: '100px' }}
          />
        ) : (
          <input className={cx(['crc-inline-edit__input', inputBaseStyle(size), size])} {...inputProps} />
        )}
        {label ? (
          <label className={cx('crc-inline-edit__label', labelStyle)} htmlFor={inputId}>
            {label}
          </label>
        ) : null}
        {!hasOnChange ? (
          <div className={cx('crc-inline-edit__right-button', rightAddOn)}>
            <button onClick={handleSaveClick} aria-label="save" disabled={Boolean(validationError)}>
              <Check color={cvar('color-inline-edit-border')} />
            </button>
            <button onClick={handleCancelClick} aria-label="cancel">
              <CloseSvg color={cvar('color-inline-edit-border')} />
            </button>
          </div>
        ) : null}
      </>
    );

    // For disabled state we add the disabled class to the parent
    // In addition we pass a <p> without the onClick handler
    const inlineDisplay = (
      <InlineButton
        onClick={disabled ? () => {} : handleActiveClick}
        className={cx('crc-inline-edit--inactive', { disabled })}
      >
        <p className={size}>
          {displayValue || placeholder}
          {contentJustSaved && (
            <div className={cx('crc-inline-edit__success-icon', successIconWrapper)}>
              <SuccessSvg color={cvar('color-background-success')} />
            </div>
          )}
          {!disabled && (
            <div className="crc-inline-edit__edit-icon">
              <PencilSvg size={'1em'} color={cvar('color-inline-edit-border')} />
            </div>
          )}
        </p>
        {label ? <div className={labelStyle}>{label}</div> : null}
      </InlineButton>
    );

    return (
      <>
        <div className={parentClassName} style={inputStyle}>
          {showInput ? inlineInput : inlineDisplay}
          {validationError ? (
            <div
              data-testid="error-message"
              className={cx('crc-inline-edit__error', errorMessage)}
              ref={errorMessageRef}
            >
              {validationError}
            </div>
          ) : null}
        </div>
        <div className={size} ref={valueSizeRef} style={sizeStyle}>
          {displayValue}
        </div>
        <div className={size} ref={placeholderSizeRef} style={sizeStyle}>
          {placeholder || ''}
        </div>
      </>
    );
  },
);

InlineEdit.displayName = 'InlineEdit';
